import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, type Lead, type ReferrerStats } from '../lib/api'
import type { User } from '../lib/auth'
import { getProduct } from '../lib/products'
import { useI18n } from '../lib/i18n'

export default function Dashboard({ user }: { user: User }) {
  const { t } = useI18n()
  const [stats, setStats] = useState<ReferrerStats | null>(null)
  const [leads, setLeads] = useState<Lead[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const shareLink = `https://getqordo.com/api/r/${user.referralCode}`

  useEffect(() => {
    Promise.all([api.get<ReferrerStats>('/api/referrers/me/stats'), api.get<{ leads: Lead[] }>('/api/leads')])
      .then(([s, l]) => {
        setStats(s)
        setLeads(l.leads)
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false))
  }, [])

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareLink)
    } catch {
      // clipboard API may be blocked — fall back to a temp textarea
      const ta = document.createElement('textarea')
      ta.value = shareLink
      document.body.appendChild(ta)
      ta.select()
      try { document.execCommand('copy') } catch { /* ignore */ }
      document.body.removeChild(ta)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  return (
    <section className="section py-10">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white">{t('Hi,')} {user.fullName.split(' ')[0] || t('partner')}</h1>
          <p className="text-ink-500 mt-1">{t('Your referral code:')} <span className="font-mono text-brand-500">{user.referralCode}</span></p>
        </div>
        <Link to="/catalog" className="btn-primary">{t('Find a business →')}</Link>
      </header>

      {/* Shareable referral link — the influencer/online channel */}
      <div className="card mb-8 border-brand-500/30 bg-gradient-to-br from-brand-600/10 to-transparent">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h2 className="text-sm font-semibold text-white">{t('🔗 Your referral link')}</h2>
          <span className="text-xs text-ink-500">{t('Share it anywhere — every click is tracked below.')}</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            readOnly
            value={shareLink}
            onFocus={(e) => e.currentTarget.select()}
            className="input flex-1 font-mono text-sm"
            aria-label="Your referral link"
          />
          <button onClick={copyLink} className="btn-primary shrink-0 min-w-[120px]">
            {copied ? t('✓ Copied') : t('Copy link')}
          </button>
        </div>
        <p className="text-xs text-ink-500 mt-2">
          {t('Tip: add')} <span className="font-mono text-ink-100">?p=barbershop</span> {t('to send people straight to one demo.')}
        </p>

        {/* Direct signup links for the real self-serve products. A business
            signing up through these gets a DOUBLED free trial (60 days) via
            your code — that discount is what protects your commission: they
            want to use your code, and the lead lands here automatically. */}
        <div className="mt-4 border-t border-ink-800 pt-4">
          <h3 className="text-sm font-semibold text-white mb-1">{t('⚡ Instant-signup products')}</h3>
          <p className="text-xs text-ink-500 mb-3">
            {t('These products are live — a business can sign up on the spot. Through your link they get a')}{' '}
            <span className="text-emerald-400 font-semibold">{t('60-day free trial instead of 30')}</span>{t(', and the deal is credited to you automatically.')}
          </p>
          <div className="space-y-2">
            {[
              { name: '💇 StyleStudio (hair salons)', url: `https://style.myclienta.com/signup?ref=${user.referralCode}` },
              { name: '📱 QR-MENU (cafés & restaurants)', url: `https://menu.myclienta.com/signup?ref=${user.referralCode}` },
            ].map((p) => (
              <div key={p.name} className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span className="text-sm text-ink-100 sm:w-64 shrink-0">{p.name}</span>
                <input
                  readOnly
                  value={p.url}
                  onFocus={(e) => e.currentTarget.select()}
                  className="input flex-1 font-mono text-xs py-2"
                  aria-label={`${p.name} signup link`}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-ink-500">{t('Loading…')}</p>
      ) : err ? (
        <div className="card border-red-500/30 text-red-400">{err}</div>
      ) : (
        <>
          <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6 mb-10">
            <Stat label={t('Link clicks')} value={stats?.clicks ?? 0} />
            <Stat label={t('Leads submitted')} value={stats?.leadsTotal ?? 0} />
            <Stat label={t('Deals won')} value={stats?.leadsWon ?? 0} />
            <Stat label={t('Conversion')} value={`${stats?.conversionRate ?? 0}%`} />
            <Stat label={t('Pending €')} value={`€${(stats?.commissionPending ?? 0).toFixed(2)}`} />
            <Stat label={t('Paid €')} value={`€${(stats?.commissionPaid ?? 0).toFixed(2)}`} tone="good" />
          </div>
          <div className="text-xs text-ink-500 -mt-6 mb-10">
            {t('Leaderboard rank:')} <span className="text-brand-500 font-semibold">#{stats?.rank ?? 1}</span>
            {' · '}{t('Click → lead rate:')} <span className="text-ink-100 font-semibold">{stats?.clickToLeadRate ?? 0}%</span>
          </div>

          <h2 className="text-xl font-semibold text-white mb-4">{t('Your leads')}</h2>
          {leads.length === 0 ? (
            <div className="card text-center">
              <p className="text-ink-100">{t('No leads yet.')} <Link to="/catalog" className="text-brand-500 underline">{t('Browse the catalog')}</Link> {t('and submit your first one.')}</p>
            </div>
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-ink-500 border-b border-ink-800">
                  <tr>
                    <th className="pb-2">{t('Business')}</th>
                    <th className="pb-2">{t('Product')}</th>
                    <th className="pb-2">{t('Plan')}</th>
                    <th className="pb-2">{t('Status')}</th>
                    <th className="pb-2 text-right">{t('Commission')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-800">
                  {leads.map((l) => {
                    const prod = getProduct(l.productSlug)
                    return (
                      <tr key={l.id}>
                        <td className="py-3">
                          <div className="font-medium text-white">{l.businessName}</div>
                          <div className="text-xs text-ink-500">{l.city}, {l.country}</div>
                        </td>
                        <td className="py-3">{prod?.icon} {prod?.name ?? l.productSlug}</td>
                        <td className="py-3 capitalize">{l.plan}</td>
                        <td className="py-3"><StatusChip status={l.status} /></td>
                        <td className="py-3 text-right">
                          <span className="text-emerald-400 font-semibold">
                            €{(l.commissionSetup + l.commissionMonthly * 6 + (l.commissionDomain ?? 0)).toFixed(2)}
                          </span>
                          {l.customDomain && (
                            <div className="text-[10px] text-amber-400 mt-0.5">{t('🌐 incl. domain')}</div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  )
}

function Stat({ label, value, tone }: { label: string; value: string | number; tone?: 'good' }) {
  return (
    <div className="card">
      <div className={`text-2xl font-bold ${tone === 'good' ? 'text-emerald-400' : 'text-white'}`}>{value}</div>
      <div className="text-xs text-ink-500 mt-1">{label}</div>
    </div>
  )
}

export function StatusChip({ status }: { status: Lead['status'] }) {
  const map: Record<Lead['status'], string> = {
    new: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
    contacted: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    won: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    lost: 'bg-ink-800 text-ink-500 border-ink-800',
    paid: 'bg-brand-500/10 text-brand-500 border-brand-500/20',
  }
  return <span className={`chip border capitalize ${map[status]}`}>{status}</span>
}
